import { Helmet } from 'react-helmet-async'
import LocalizedLink from '@/components/layout/LocalizedLink'
import { useTranslation } from 'react-i18next'
import { Clock, Calculator, Heart } from 'lucide-react'
import Breadcrumb from '@/components/layout/Breadcrumb'
import Card from '@/components/ui/Card'
import { cn } from '@/utils/cn'

export default function ServicesPage() {
  const { t } = useTranslation()

  const services = [
    {
      id: 'prayer',
      title: t('prayer.title', 'أوقات الصلاة'),
      description: t('prayer.description', 'اعرف أوقات الصلاة اليومية حسب مدينتك'),
      icon: Clock,
      link: '/services/prayer',
      color: 'bg-blue-500',
      gradient: 'from-blue-500 to-blue-600',
    },
    {
      id: 'zakat',
      title: t('zakat.title', 'حاسبة الزكاة'),
      description: t('zakat.description', 'احسب زكاتك بسهولة ودقة'),
      icon: Calculator,
      link: '/services/zakat',
      color: 'bg-green-500',
      gradient: 'from-green-500 to-green-600',
    },
    {
      id: 'donations',
      title: t('donations.title', 'التبرعات'),
      description: t('donations.description', 'تبرع لدعم المشاريع الخيرية'),
      icon: Heart,
      link: '/services/donations',
      color: 'bg-red-500',
      gradient: 'from-red-500 to-red-600',
    },
  ]

  const breadcrumbItems = [
    { label: t('common.home'), to: '/' },
    { label: t('common.services', 'الخدمات') },
  ]

  return (
    <>
      <Helmet>
        <title>{t('common.services', 'الخدمات')} - سكينة</title>
        <meta
          name="description"
          content={t('services.description', 'استكشف خدماتنا: أوقات الصلاة، حاسبة الزكاة، والتبرعات')}
        />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={breadcrumbItems} className="mb-6" />

        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            {t('common.services', 'الخدمات')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {t('services.intro', 'اختر الخدمة التي تريد استخدامها')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services
            .filter((service) => service.id !== 'donations') // Hide donations
            .map((service) => {
            const Icon = service.icon
            return (
              <LocalizedLink
                key={service.id}
                to={service.link}
                className="block group"
              >
                <Card className="h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="p-6">
                    {/* Icon */}
                    <div
                      className={cn(
                        'w-16 h-16 rounded-full flex items-center justify-center mb-4',
                        `bg-gradient-to-br ${service.gradient} text-white shadow-lg`
                      )}
                    >
                      <Icon className="w-8 h-8" />
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {service.title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      {service.description}
                    </p>

                    {/* Link Arrow */}
                    <div className="flex items-center text-primary-600 dark:text-primary-400 font-medium group-hover:gap-2 transition-all">
                      <span>{t('common.learnMore', 'اعرف المزيد')}</span>
                      <svg
                        className="w-5 h-5 rtl:rotate-180 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </Card>
              </LocalizedLink>
            )
          })}
        </div>
      </div>
    </>
  )
}

